import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

const tocItems = [
  { id: 'what', label: 'tocWhat' },
  { id: 'how', label: 'tocHow' },
  { id: 'categories', label: 'tocCategories' },
  { id: 'qc', label: 'tocQc' },
  { id: 'shipping', label: 'tocShipping' },
  { id: 'safety', label: 'tocSafety' },
  { id: 'table', label: 'tocTable' },
  { id: 'faq', label: 'tocFaq' },
];

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const prefix = i18n.language === 'pt' ? '' : '/' + i18n.language;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [i18n.language]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Breadcrumb + Hero */}
        <section className="pt-24 md:pt-28 pb-8 md:pb-12 bg-background-50 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <a href={prefix + '/'} className="hover:text-primary-600 transition-colors">{t('article.breadcrumbHome')}</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{t('article.breadcrumbArticle')}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {t('article.h1')}
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {t('article.updated')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-shield-check-line"></i>
                {t('article.reviewed')}
              </span>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid lg:grid-cols-[1fr_280px] gap-8 lg:gap-12">
            {/* Main Content */}
            <article>
              {/* Quick Answer */}
              <div className="bg-primary-50/60 border-l-4 border-primary-500 rounded-r-lg p-5 md:p-6 mb-8 md:mb-10">
                <p className="text-sm font-semibold text-primary-700 uppercase tracking-wide mb-2">
                  Quick Answer
                </p>
                <p className="text-base text-foreground-700 leading-relaxed">
                  {t('article.quickAnswer')}
                </p>
              </div>

              {/* TOC */}
              <div className="bg-background-100 rounded-lg p-5 md:p-6 mb-8 md:mb-10">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  {t('article.toc')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {tocItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className="text-left text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1"
                    >
                      <i className="ri-arrow-right-s-line mr-1 text-primary-400"></i>
                      {t(`article.${item.label}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* What is CSSBuy Spreadsheet? */}
              <section id="what" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2What')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2WhatP1')}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {t('article.h2WhatP2')}
                </p>
                <a href={prefix + '/'} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  <i className="ri-home-line mr-1"></i>
                  {t('article.breadcrumbHome')}
                </a>
              </section>

              {/* How to Use */}
              <section id="how" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2How')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2HowP1')}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {t('article.h2HowP2')}
                </p>
                <div className="bg-accent-50/50 rounded-lg p-5 md:p-6 mb-4">
                  <p className="text-sm text-accent-800 font-medium mb-1">
                    <i className="ri-lightbulb-line mr-1"></i>
                    {t('article.tipLabel')}
                  </p>
                  <p className="text-sm text-accent-700">
                    {t('article.tipText')}
                  </p>
                </div>
              </section>

              {/* Categories */}
              <section id="categories" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2Categories')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2CategoriesP1')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50/50 transition-colors group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
                        <i className={cat.icon}></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors">
                        {t(`categories.${cat.nameKey}`)}
                      </span>
                      <i className="ri-external-link-line ml-auto text-foreground-400 text-xs"></i>
                    </a>
                  ))}
                </div>
                <a href={prefix + '/'} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  <i className="ri-home-line mr-1"></i>
                  {t('article.breadcrumbHome')}
                </a>
              </section>

              {/* QC */}
              <section id="qc" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2Qc')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2QcP1')}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {t('article.h2QcP2')}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary text-sm"
                >
                  {t('article.ctaSpreadsheet')}
                  <i className="ri-external-link-line ml-1.5"></i>
                </a>
              </section>

              {/* Shipping */}
              <section id="shipping" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2Shipping')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2ShippingP1')}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {t('article.h2ShippingP2')}
                </p>
                <a href={prefix + '/'} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  <i className="ri-home-line mr-1"></i>
                  {t('article.breadcrumbHome')}
                </a>
              </section>

              {/* Safety */}
              <section id="safety" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2Safety')}
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article.h2SafetyP1')}
                </p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6">
                  {t('article.h2SafetyP2')}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-secondary text-sm"
                >
                  {t('article.ctaSafety')}
                  <i className="ri-arrow-right-line ml-1.5"></i>
                </a>
              </section>

              {/* Comparison Table */}
              <section id="table" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {t('article.h2Table')}
                </h2>
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border-b border-background-200">
                          {t('article.feature')}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-primary-700 border-b border-background-200">
                          {t('article.spreadsheet')}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-600 border-b border-background-200">
                          {t('article.normalSearch')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { f: 'article.prodDiscovery', s: 'article.prodDiscoveryS', n: 'article.prodDiscoveryN' },
                        { f: 'article.organization', s: 'article.organizationS', n: 'article.organizationN' },
                        { f: 'article.categorySearch', s: 'article.categorySearchS', n: 'article.categorySearchN' },
                        { f: 'article.qcRef', s: 'article.qcRefS', n: 'article.qcRefN' },
                        { f: 'article.beginnerFriendly', s: 'article.beginnerFriendlyS', n: 'article.beginnerFriendlyN' },
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-background-100 hover:bg-background-50/80 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-800">{t(row.f)}</td>
                          <td className="px-4 py-3 text-primary-700 font-medium">{t(row.s)}</td>
                          <td className="px-4 py-3 text-foreground-600">{t(row.n)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  {t('article.h2Faq')}
                </h2>
                <div className="space-y-4">
                  {[
                    { q: 'article.faq1Q', a: 'article.faq1A' },
                    { q: 'article.faq2Q', a: 'article.faq2A' },
                    { q: 'article.faq3Q', a: 'article.faq3A' },
                    { q: 'article.faq4Q', a: 'article.faq4A' },
                    { q: 'article.faq5Q', a: 'article.faq5A' },
                  ].map((faq, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                        {t(faq.q)}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {t(faq.a)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Bottom CTA */}
              <div className="bg-gradient-to-br from-primary-50 to-accent-50/50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                  CSSBuy Spreadsheet
                </h3>
                <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
                  {t('article.bottomCtaText')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary text-sm"
                  >
                    {t('article.ctaMain')}
                    <i className="ri-external-link-line ml-1.5"></i>
                  </a>
                  <a
                    href={prefix + '/'}
                    className="btn-secondary text-sm"
                  >
                    <i className="ri-home-line mr-1.5"></i>
                    {t('article.breadcrumbHome')}
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-6">
                {/* TOC Sidebar */}
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-3">
                    {t('article.toc')}
                  </h3>
                  <div className="space-y-1">
                    {tocItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollTo(item.id)}
                        className="block w-full text-left text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1.5"
                      >
                        {t(`article.${item.label}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Sidebar */}
                <div className="bg-primary-50 rounded-lg p-5">
                  <h3 className="font-heading text-base font-semibold text-primary-800 mb-2">
                    CSSBuy Spreadsheet
                  </h3>
                  <p className="text-sm text-primary-700 mb-4">
                    {t('article.sidebarCtaText')}
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary text-sm w-full justify-center"
                  >
                    {t('article.ctaSpreadsheet')}
                    <i className="ri-external-link-line ml-1.5"></i>
                  </a>
                </div>

                {/* Categories Sidebar */}
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-3">
                    {t('article.sidebarCategories')}
                  </h3>
                  <div className="space-y-2">
                    {categories.slice(0, 7).map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                      >
                        <i className={`${cat.icon} text-primary-400 text-xs`}></i>
                        {t(`categories.${cat.nameKey}`)}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Home Link */}
                <a
                  href={prefix + '/'}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  <i className="ri-home-line"></i>
                  {t('article.breadcrumbHome')}
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}